import { Injectable } from '@angular/core';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PollingService {
  private static readonly INITIAL_POLLING_DELAY_MS: number = 1000; // 1 second
  private static readonly MAX_POLLING_DELAY_MS: number = 30000; // 30 seconds

  /**
   * Starts a polling loop that will continue until the stop condition returns true or the destroy$ emits.
   *
   * @param pollFn - Function to execute on each poll tick
   * @param stopCondition - Function to determine when polling should stop
   * @param destroy$ - Stream to signal when polling should be cancelled
   * @returns A subscription that manages polling
   */
  public startPolling(
    pollFn: () => void,
    stopCondition: () => boolean,
    destroy$: Subject<void>,
  ): Subscription {
    let attemptCount: number = 0;
    let pollingSub: Subscription;

    const poll = (): void => {
      if (stopCondition()) {
        pollingSub?.unsubscribe();
        return;
      }

      pollFn();
      attemptCount++;

      const delay: number = Math.min(
        PollingService.INITIAL_POLLING_DELAY_MS * Math.pow(2, attemptCount - 1),
        PollingService.MAX_POLLING_DELAY_MS,
      );

      pollingSub = timer(delay)
        .pipe(takeUntil(destroy$))
        .subscribe(() => poll());
    };

    pollingSub = timer(0)
      .pipe(takeUntil(destroy$))
      .subscribe(() => poll());

    return pollingSub;
  }
}
